import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { Badge, tokens } from "@blurb/codex-react";
import { Box, createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
type BadgeColor = "blue" | "purple" | "green" | "orange" | "red" | "white" | "gray";

type CellStatusConfig<T extends string> = Record<
  T,
  {
    name?: string;
    color?: BadgeColor;
    iconColor?: string;
  }
>;

type CellStatusType<T extends string> = React.FC<{ status: CellStatusBaseProps<T>["status"] }>;
const useStyles = createStyles(() => ({
  badgeWrapper: {
    display: "inline-flex",
    alignItems: "center",
  },
}));

interface CellStatusBaseProps<T extends string> {
  status?: T | null;
  configs: CellStatusConfig<T>;
  badge?: boolean;
}
function CellStatusBase<T extends string>({ status, configs, badge }: CellStatusBaseProps<T>): React.ReactElement {
  const { classes } = useStyles();
  const config = status && configs[status];
  if (!status || !config) return <></>;

  return (
    <Box className={classes.badgeWrapper}>
      {badge ? (
        <Badge
          label={config.name || status.toLowerCase()}
          color={config.color}
          variant="round"
          size="small"
          border={true}
          icon={
            config.iconColor ? (
              <IconV3 icon="circle" size={12} color={config.iconColor} style={{ fontVariationSettings: "'FILL' 1" }} />
            ) : undefined
          }
        />
      ) : (
        config.name || status.toLowerCase()
      )}
    </Box>
  );
}

const PublicStatusColor = {
  RECEIVED: "blue" as const,
  VALIDATING: "orange" as const,
  SHIPPED: "green" as const,
  CANCELLED: "white" as const,
  FAILED: "red" as const,
};

/* Public Order Status */
export const publicOrderStatusConfig: CellStatusConfig<GraphQL.PublicOrderStatus> = {
  [GraphQL.PublicOrderStatus.Received]: {
    name: "Received",
    color: PublicStatusColor.RECEIVED,
    iconColor: tokens.color.primitive.light.blue[700],
  },
  [GraphQL.PublicOrderStatus.Validating]: {
    name: "Validating",
    color: PublicStatusColor.VALIDATING,
    iconColor: tokens.color.primitive.light.yellow[800],
  },
  [GraphQL.PublicOrderStatus.ValidHoldingBin]: {
    name: "Valid - Holding Bin",
    color: PublicStatusColor.VALIDATING,
    iconColor: tokens.color.primitive.light.yellow[800],
  },
  [GraphQL.PublicOrderStatus.ValidAwaitingPayment]: {
    name: "Valid - Awaiting Payment",
    color: PublicStatusColor.VALIDATING,
    iconColor: tokens.color.primitive.light.yellow[800],
  },
  [GraphQL.PublicOrderStatus.Printing]: {
    name: "Printing",
    color: PublicStatusColor.RECEIVED,
    iconColor: tokens.color.primitive.light.blue[700],
  },
  [GraphQL.PublicOrderStatus.Shipped]: {
    name: "Shipped",
    color: PublicStatusColor.SHIPPED,
    iconColor: tokens.color.primitive.light.green[700],
  },
  [GraphQL.PublicOrderStatus.Failed]: {
    name: "Failed",

    color: PublicStatusColor.FAILED,
    iconColor: tokens.color.primitive.light.red[700],
  },
  [GraphQL.PublicOrderStatus.Cancelled]: {
    name: "Cancelled",
    color: PublicStatusColor.CANCELLED,
    iconColor: tokens.color.primitive.light.gray[900],
  },
  [GraphQL.PublicOrderStatus.CancellationRequested]: {
    name: "Cancellation Requested",
    color: PublicStatusColor.VALIDATING,
    iconColor: tokens.color.primitive.light.yellow[800],
  },
  [GraphQL.PublicOrderStatus.Exception]: {
    name: "Exception",
    color: PublicStatusColor.FAILED,
    iconColor: tokens.color.primitive.light.red[700],
  },
  [GraphQL.PublicOrderStatus.Queued]: {
    name: "Queued",
    color: PublicStatusColor.RECEIVED,
    iconColor: tokens.color.primitive.light.blue[700],
  },
  [GraphQL.PublicOrderStatus.Rejected]: {
    name: "Rejected",
    color: PublicStatusColor.FAILED,
    iconColor: tokens.color.primitive.light.red[700],
  },
};

const CellStatusPublicOrder: CellStatusType<GraphQL.PublicOrderStatus> = ({ status }) => (
  <CellStatusBase status={status} configs={publicOrderStatusConfig} badge={true} />
);

/* Payment Status */
export const paymentStatusConfig: CellStatusConfig<GraphQL.PaymentStatus> = {
  [GraphQL.PaymentStatus.ExternallyPaid]: {
    name: "Externally Paid",
  },
  [GraphQL.PaymentStatus.Paid]: {
    name: "Paid",
  },
  [GraphQL.PaymentStatus.Unpaid]: {
    name: "Not Paid",
  },
  // INVESTIGATE THIS STATUS
  [GraphQL.PaymentStatus.NoPaymentStaffOrder]: {
    name: "No Payment - Staff Order",
  },
  [GraphQL.PaymentStatus.PaymentPending]: {
    name: "No Payment - Payment Pending",
  },
};

const CellStatusPayment: CellStatusType<GraphQL.PaymentStatus> = ({ status }) => (
  <CellStatusBase status={status} configs={paymentStatusConfig} />
);

export const CellStatusV2 = {
  PublicOrder: CellStatusPublicOrder,
  Payment: CellStatusPayment,
};
